import { ReactNode, Suspense, useEffect, useRef, useState } from 'react';
import { Drawer } from 'react-daisyui';
import { useDispatch } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';
import '../App.css';
import { AnnouncementBanner } from '../components/AnnouncementBanner';
import ErrorModal from '../components/ErrorModal';
import { AppLoader } from '../components/Loader';
import AppHeaderV2 from '../components/navigation/AppHeaderV2';
import AppSidebar from '../components/navigation/AppSidebar';
import { reduxServices } from '../reducers/reduxServices';
import { useTypedSelector } from '../stateStore';

export interface IDefaultLayoutProps {
  bannerClassName?: string;
  children?: ReactNode;
  hasBreadcrumbs?: boolean;
  hasWorkspaceIcons?: boolean;
  initialCrumb?: string;
  mainWrapperBgColor?: 'neutral' | 'white';
  navbarElement?: ReactNode;
}

const usePreviousLocation = () => {
  const location = useLocation();
  const prevLocationRef = useRef(location);

  useEffect(() => {
    prevLocationRef.current = location;
  }, [location]);

  return prevLocationRef.current;
};

const DefaultLayoutV3 = ({
  // Matches DefaultLayoutBody below. Override where a page insets its own
  // content further, so the banner keeps lining up with it.
  bannerClassName = 'px-5 pt-5 xl:mx-auto xl:max-w-screen-4xl',
  children,
  hasBreadcrumbs,
  initialCrumb,
  mainWrapperBgColor = 'neutral',
  navbarElement,
}: IDefaultLayoutProps) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const dispatch = useDispatch();

  const handleMenuToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const showErrorModal = useTypedSelector(
    reduxServices.globalState.selectors.showErrorModal,
  );

  const previousLocation = usePreviousLocation();
  const currentLocation = useLocation();

  useEffect(() => {
    if (previousLocation.pathname !== currentLocation.pathname) {
      dispatch(reduxServices.globalState.actions.setShowErrorPage(false));
    }
  }, [currentLocation, previousLocation, dispatch]);

  return (
    <Suspense fallback={<AppLoader />}>
      <Drawer
        className="lg:drawer-open"
        contentClassName="overflow-x-auto flex flex-col relative !z-auto"
        open={isSidebarOpen}
        side={<AppSidebar>{navbarElement}</AppSidebar>}
        onClickOverlay={handleMenuToggle}
      >
        <div className="overflow-y-hidden">
          <AppHeaderV2
            hasBreadcrumbs={hasBreadcrumbs}
            hasLogo={false}
            hasMenuToggle={true}
            initialCrumb={initialCrumb}
            onMenuToggle={handleMenuToggle}
          />
          <DefaultLayoutMainWrapper mainWrapperBgColor={mainWrapperBgColor}>
            <AnnouncementBanner className={bannerClassName} />
            {children ?? (
              <DefaultLayoutBody>
                <DefaultLayoutContent>
                  <Outlet />
                </DefaultLayoutContent>
              </DefaultLayoutBody>
            )}
            <ErrorModal isOpen={showErrorModal} />
          </DefaultLayoutMainWrapper>
        </div>
      </Drawer>
    </Suspense>
  );
};

export interface IDefaultLayoutMainWrapperProps {
  children: ReactNode;
  mainWrapperBgColor?: 'neutral' | 'white';
}

export const DefaultLayoutMainWrapper = ({
  children,
  mainWrapperBgColor = 'neutral',
}: IDefaultLayoutMainWrapperProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollToTop = useTypedSelector(
    reduxServices.globalState.selectors.scrollToTop,
  );

  useEffect(() => {
    if (!contentRef.current || !scrollToTop) return;

    contentRef.current.scroll({
      behavior: 'smooth',
      top: 0,
    });
  }, [scrollToTop]);

  return (
    <main
      className={`flex-grow-1 h-screen overflow-y-scroll ${
        mainWrapperBgColor === 'neutral' ? 'bg-neutral' : 'bg-sr-white'
      }`}
      ref={contentRef}
    >
      <div className="pb-36">{children}</div>
    </main>
  );
};

export interface IDefaultLayoutHeadingProps {
  children: ReactNode;
}

export const DefaultLayoutHeading = ({
  children,
}: IDefaultLayoutHeadingProps) => {
  return (
    <div className="w-full border-b border-sr-outline bg-white px-5 py-5">
      <div className="text-center md:text-left xl:mx-auto xl:max-w-5xl 2xl:px-0">
        {children}
      </div>
    </div>
  );
};

export const DefaultLayoutBody = ({ children }: React.PropsWithChildren) => {
  return (
    <div className="flex h-full py-8 md:px-5 xl:mx-auto xl:max-w-screen-4xl">
      <div className="flex w-full flex-col gap-1.5">{children}</div>
    </div>
  );
};

export const DefaultLayoutContent = ({ children }: React.PropsWithChildren) => {
  return (
    <div className="flex h-auto w-full flex-col items-start gap-[30px] self-stretch bg-white p-5 md:rounded-xl md:border md:border-sr-outline xl:mx-auto xl:max-w-screen-4xl">
      {children}
    </div>
  );
};

export default Object.assign(DefaultLayoutV3, {
  Body: DefaultLayoutBody,
  Content: DefaultLayoutContent,
  Heading: DefaultLayoutHeading,
  MainWrapper: DefaultLayoutMainWrapper,
});
